/**
 * @fileoverview Data-driven variant selection (MOR-886).
 *
 * A variant group may carry an optional data trigger (`fieldBinding`) that resolves the
 * group's active option per generated document from that document's field
 * values (e.g. "contact_security_interest_pmsi" = "Yes" → the "pmsi"
 * option, otherwise the "allpap" option). This lets one generation run
 * produce different wording per recipient without the practitioner picking
 * a variant by hand for every document.
 *
 * Precedence, most specific wins:
 *   explicit per-document selection > data trigger > job-wide selection.
 *
 * Data triggers are evaluated at generation time (the wizard stamps the resolved
 * option into the document's `variantSelections`), so the existing render
 * pipeline — `resolveVariantBlocks` and the day-one per-block overrides —
 * keeps working unchanged and manual re-selection after generation still
 * takes precedence.
 *
 * Dependency-free by design (like contactFields.ts) — imported by both the
 * Convex backend and the frontend wizard/renderer.
 */

export interface VariantFieldBindingMapping {
  /** Field value to match (compared trimmed, case-insensitively). */
  value: string;
  /** variantGroups.options[].value applied on match. */
  optionValue: string;
}

export interface VariantFieldBinding {
  /** Field key looked up in the document's resolved field values. */
  fieldKey: string;
  mappings: Array<VariantFieldBindingMapping>;
  /** Option applied when no mapping matches; unset = leave unselected. */
  defaultOptionValue?: string;
}

/** The subset of a variantGroups row the binding resolver needs. */
export interface VariantGroupWithBinding {
  slug: string;
  fieldBinding?: VariantFieldBinding | null;
}

/**
 * Normalise a field value for comparison: trim + lowercase, with empty
 * treated as absent. "YES", " yes " and "Yes" all match a mapping value
 * of "Yes" — practitioners type display values, not canonical tokens.
 */
function normalizeBindingValue(
  value: string | undefined | null,
): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve one binding against a document's field values. Returns the
 * matched option value, the binding's default when nothing matches, or
 * `null` when the binding cannot decide (caller falls back to manual /
 * job-wide selection).
 */
export function resolveVariantBinding(
  binding: VariantFieldBinding,
  fieldValues: Record<string, string | undefined>,
): string | null {
  const actual = normalizeBindingValue(fieldValues[binding.fieldKey]);
  if (actual !== null) {
    for (const mapping of binding.mappings) {
      if (normalizeBindingValue(mapping.value) === actual) {
        return mapping.optionValue;
      }
    }
  }
  return binding.defaultOptionValue ?? null;
}

/**
 * Resolve every bound group for one document. `groupSlugs` limits work to
 * the groups actually referenced by the template being generated;
 * `manualSelections` (per-document overrides picked in the wizard) win over
 * the binding. Returns only the slugs the bindings decided — merge the
 * result over job-wide selections, under manual per-doc selections.
 */
export function resolveBoundVariantSelections(
  groups: ReadonlyArray<VariantGroupWithBinding>,
  groupSlugs: ReadonlyArray<string>,
  fieldValues: Record<string, string | undefined>,
  manualSelections?: Record<string, string | null | undefined>,
): Record<string, string> {
  const bySlug = new Map(groups.map((g) => [g.slug, g]));
  const resolved: Record<string, string> = {};
  for (const slug of groupSlugs) {
    // An explicit manual choice (including explicit "None" = null) wins.
    if (manualSelections && slug in manualSelections) continue;
    const binding = bySlug.get(slug)?.fieldBinding;
    if (!binding) continue;
    const option = resolveVariantBinding(binding, fieldValues);
    if (option !== null) resolved[slug] = option;
  }
  return resolved;
}
